// Using new util master in place of util in @types@node since all the below in previously used util are now deprecated
export function isObject(value: any) {
    return value !== null && typeof value === "object";
}
export function isNumber(value: any) {
    return value !== null && typeof value === "number";
}
export function isUndefined(value: any) {
    return value === undefined;
}
export function isNullOrUndefined(value: any) {
    return value === null || value === undefined;
}
export function isNull(value: any) {
    return value === null;
}

export function sortArrayByName(list) {
    if (list[0]?.name) {
        list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
}

// HydroSHEDS overlay dataset ids: hybas_lev01_v1c .. hybas_lev11_v1c, hybas_lev12_v1c_v3
const HYDROSHEDS_ID_PATTERN = /^hybas_lev(\d+)_/i;

export function getHydroshedsLevel(datasetId: string): number | null {
    const match = typeof datasetId === 'string' ? datasetId.match(HYDROSHEDS_ID_PATTERN) : null;
    return match ? Number(match[1]) : null;
}

/**
 * Numeric comparison of two HydroSHEDS overlays by dataset id, so Level 10-12 come after Level 9.
 * Returns null when either id is not a HydroSHEDS level, letting the caller apply its usual comparison.
 */
export function compareHydroshedsLevels(datasetId1: string, datasetId2: string): number | null {
    const level1 = getHydroshedsLevel(datasetId1);
    const level2 = getHydroshedsLevel(datasetId2);
    return level1 !== null && level2 !== null ? level1 - level2 : null;
}

/**
 * Puts the HydroSHEDS overlays of a list in level order, reusing the positions they already hold,
 * so every other item keeps its place. Sorts in place and returns the list.
 */
export function sortHydroshedsLevels<T>(list: T[], getDatasetId: (item: T) => string): T[] {
    const positions: number[] = [];
    list.forEach((item, index) => {
        if (getHydroshedsLevel(getDatasetId(item)) !== null) {
            positions.push(index);
        }
    });
    const levels = positions
        .map(index => list[index])
        .sort((a, b) => compareHydroshedsLevels(getDatasetId(a), getDatasetId(b)) ?? 0);
    positions.forEach((position, index) => list[position] = levels[index]);
    return list;
}

export function decodeHtmlText(value: string): string {
    if (!value) return '';
    const htmlEntities: { [key: string]: string } = {
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&#39;': "'",
        '&apos;': "'",
        '&nbsp;': ' ',
        '&copy;': '©',
        '&reg;': '®',
        '&trade;': '™',
        '&euro;': '€',
        '&pound;': '£',
        '&yen;': '¥',
        '&cent;': '¢',
        '&sect;': '§',
        '&para;': '¶',
        '&plusmn;': '±',
        '&times;': '×',
        '&divide;': '÷',
        '&deg;': '°',
        '&micro;': 'µ',
        '&middot;': '·',
        '&hellip;': '…',
        '&ndash;': '–',
        '&mdash;': '—',
        '&lsquo;': '\'\'',
        '&rsquo;': '\'\'',
        '&ldquo;': '"',
        '&rdquo;': '"',
        '&laquo;': '«',
        '&raquo;': '»',
        '&acute;': '´',
        '&grave;': '`',
        '&circ;': '^',
        '&tilde;': '~',
        '&uml;': '¨',
        '&cedil;': '¸',
        '&aring;': 'å',
        '&aelig;': 'æ',
        '&oslash;': 'ø',
        '&ccedil;': 'ç',
        '&eacute;': 'é',
        '&egrave;': 'è',
        '&ecirc;': 'ê',
        '&euml;': 'ë',
        '&aacute;': 'á',
        '&agrave;': 'à',
        '&acirc;': 'â',
        '&auml;': 'ä',
        '&iacute;': 'í',
        '&igrave;': 'ì',
        '&icirc;': 'î',
        '&iuml;': 'ï',
        '&oacute;': 'ó',
        '&ograve;': 'ò',
        '&ocirc;': 'ô',
        '&ouml;': 'ö',
        '&uacute;': 'ú',
        '&ugrave;': 'ù',
        '&ucirc;': 'û',
        '&uuml;': 'ü',
        '&ntilde;': 'ñ',
        '&szlig;': 'ß'
    };

    const singleDecode = (text: string): string => {
        let decoded = text;
        for (const entity in htmlEntities) {
            decoded = decoded.replace(new RegExp(entity, 'g'), htmlEntities[entity]);
        }
        return decoded;
    };

    // Decode once
    let decoded = singleDecode(value);

    // Check if it still contains any HTML entities (indicating double-encoded legacy data)
    // TODO: Remove this check after database migration to clean up legacy data
    const hasHtmlEntities = Object.keys(htmlEntities).some(entity => decoded.includes(entity));
    if (hasHtmlEntities) {
        decoded = singleDecode(decoded);
    }

    return decoded;
}
